import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { User, Users, Mail, Key, Edit3, Trash2, Plus, RefreshCw, AlertCircle, Check, X, Shield, UserCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user'
  });
  const [editFormData, setEditFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user'
  });

  const { toast } = useToast();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Charger les utilisateurs et logs
  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [usersResponse, logsResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/users`),
        axios.get(`${BACKEND_URL}/api/logs?all_logs=true`)
      ]);
      
      setUsers(usersResponse.data.users || []);
      setLogs(logsResponse.data.logs || []);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Calculer les stats par utilisateur
  const getUserStats = (username) => {
    const userLogs = logs.filter(log => log.username === username);
    const positiveRatings = userLogs.filter(log => log.rating === 1).length;
    const negativeRatings = userLogs.filter(log => log.rating === 0).length;
    
    return {
      totalRequests: userLogs.length,
      positiveRatings,
      negativeRatings,
      satisfactionRate: userLogs.length > 0 
        ? Math.round((positiveRatings / userLogs.length) * 100) 
        : 0
    };
  };

  // Créer un utilisateur
  const handleCreateUser = async () => {
    if (!formData.username || !formData.email || !formData.password) {
      toast({
        title: "Erreur",
        description: "Tous les champs sont requis",
        variant: "destructive",
      });
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/users`, formData);
      
      toast({
        title: "Succès",
        description: "Utilisateur créé avec succès",
      });

      setFormData({ username: '', email: '', password: '' });
      setShowCreateForm(false);
      loadData();
    } catch (error) {
      console.error('Erreur création utilisateur:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de la création",
        variant: "destructive",
      });
    }
  };

  // Supprimer un utilisateur
  const handleDeleteUser = async (username) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur ${username} ?`)) {
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/users/${username}`);
      
      toast({
        title: "Succès",
        description: "Utilisateur supprimé avec succès",
      });

      loadData();
    } catch (error) {
      console.error('Erreur suppression utilisateur:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de la suppression",
        variant: "destructive",
      });
    }
  };

  // Commencer l'édition d'un utilisateur
  const handleStartEdit = (user) => {
    setEditingUser(user.username);
    setEditFormData({
      username: user.username,
      email: user.email,
      password: '', // Laissé vide pour sécurité
      role: user.role || 'user'
    });
  };

  // Annuler l'édition
  const handleCancelEdit = () => {
    setEditingUser(null);
    setEditFormData({
      username: '',
      email: '',
      password: '',
      role: 'user'
    });
  };

  // Sauvegarder les modifications
  const handleUpdateUser = async (oldUsername) => {
    if (!editFormData.username || !editFormData.email) {
      toast({
        title: "Erreur",
        description: "Le username et l'email sont requis",
        variant: "destructive",
      });
      return;
    }

    try {
      const updateData = {
        username: editFormData.username,
        email: editFormData.email,
        role: editFormData.role  // Ajouter le rôle
      };

      // N'envoyer le password que s'il a été modifié
      if (editFormData.password) {
        updateData.password = editFormData.password;
      }

      await axios.put(`${BACKEND_URL}/api/users/${oldUsername}`, updateData);
      
      toast({
        title: "Succès",
        description: "Utilisateur mis à jour avec succès",
      });

      setEditingUser(null);
      setEditFormData({ username: '', email: '', password: '', role: 'user' });
      loadData();
    } catch (error) {
      console.error('Erreur modification utilisateur:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de la modification",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <Users className="w-5 h-5 text-blue-600" />
              <span>Gestion des utilisateurs</span>
              <Badge variant="secondary" className="ml-2">
                {users.length} {users.length > 1 ? 'utilisateurs' : 'utilisateur'}
              </Badge>
            </CardTitle>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={loadData}
                disabled={isLoading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Actualiser
              </Button>
              <Button
                size="sm"
                onClick={() => setShowCreateForm(!showCreateForm)}
                className="bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nouvel utilisateur
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Formulaire de création */}
      {showCreateForm && (
        <Card className="border-blue-200 bg-blue-50/30">
          <CardHeader>
            <CardTitle className="text-lg">Créer un nouvel utilisateur</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Nom d'utilisateur
                </label>
                <Input
                  placeholder="username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Email
                </label>
                <Input
                  type="email"
                  placeholder="user@aprolis.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Mot de passe
                </label>
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Rôle
                </label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateForm(false);
                  setFormData({ username: '', email: '', password: '', role: 'user' });
                }}
              >
                <X className="w-4 h-4 mr-2" />
                Annuler
              </Button>
              <Button
                onClick={handleCreateUser}
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
              >
                <Check className="w-4 h-4 mr-2" />
                Créer
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Erreur */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center space-x-3">
          <AlertCircle className="w-5 h-5" />
          <span className="text-sm font-medium">{error}</span>
        </div>
      )}

      {/* Liste des utilisateurs */}
      <div className="grid grid-cols-1 gap-4">
        {[...users].sort((a, b) => (a.username || '').localeCompare(b.username || '', 'fr')).map((user) => {
          const stats = getUserStats(user.username);
          const isEditing = editingUser === user.username;
          
          return (
            <Card key={user.username} className={`border transition-shadow ${
              isEditing ? 'border-blue-400 shadow-lg bg-blue-50/20' : 'border-slate-200 hover:shadow-md'
            }`}>
              <CardContent className="p-6">
                {isEditing ? (
                  /* Mode édition */
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-slate-800">
                        Éditer l'utilisateur
                      </h3>
                      <Badge className="bg-blue-100 text-blue-700">
                        En cours d'édition
                      </Badge>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-1 block">
                          Nom d'utilisateur
                        </label>
                        <Input
                          placeholder="username"
                          value={editFormData.username}
                          onChange={(e) => setEditFormData({ ...editFormData, username: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-1 block">
                          Email
                        </label>
                        <Input
                          type="email"
                          placeholder="user@aprolis.com"
                          value={editFormData.email}
                          onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-1 block">
                          Nouveau mot de passe
                          <span className="text-xs text-slate-500 ml-1">(optionnel)</span>
                        </label>
                        <Input
                          type="password"
                          placeholder="Laisser vide pour ne pas changer"
                          value={editFormData.password}
                          onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-1 block">
                          Rôle
                        </label>
                        <Select value={editFormData.role} onValueChange={(value) => setEditFormData({ ...editFormData, role: value })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">User</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="flex justify-end space-x-2 pt-2">
                      <Button
                        variant="outline"
                        onClick={handleCancelEdit}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Annuler
                      </Button>
                      <Button
                        onClick={() => handleUpdateUser(user.username)}
                        className="bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700"
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Sauvegarder
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* Mode affichage normal */
                  <div className="flex items-start justify-between">
                    {/* Info utilisateur */}
                    <div className="flex items-start space-x-4 flex-1">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-6 h-6 text-white" />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-semibold text-slate-800">{user.username}</h3>
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            ID: {user.username}
                          </Badge>
                          {/* Badge Rôle */}
                          {user.role === 'admin' ? (
                            <Badge className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white">
                              <Shield className="w-3 h-3 mr-1" />
                              Admin
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200">
                              <UserCircle className="w-3 h-3 mr-1" />
                              User
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex items-center space-x-2 text-sm text-slate-600 mb-3">
                          <Mail className="w-4 h-4" />
                          <span>{user.email}</span>
                        </div>

                        {/* Statistiques */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                          <div className="bg-slate-50 rounded-lg p-3">
                            <p className="text-xs text-slate-600 mb-1">Requêtes totales</p>
                            <p className="text-xl font-bold text-slate-900">{stats.totalRequests}</p>
                          </div>
                          
                          <div className="bg-green-50 rounded-lg p-3">
                            <p className="text-xs text-green-600 mb-1">Feedback positif</p>
                            <p className="text-xl font-bold text-green-900">{stats.positiveRatings}</p>
                          </div>
                          
                          <div className="bg-red-50 rounded-lg p-3">
                            <p className="text-xs text-red-600 mb-1">Feedback négatif</p>
                            <p className="text-xl font-bold text-red-900">{stats.negativeRatings}</p>
                          </div>
                          
                          <div className="bg-blue-50 rounded-lg p-3">
                            <p className="text-xs text-blue-600 mb-1">Satisfaction</p>
                            <p className="text-xl font-bold text-blue-900">{stats.satisfactionRate}%</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-2 ml-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleStartEdit(user)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      >
                        <Edit3 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteUser(user.username)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* État vide */}
      {!isLoading && users.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Aucun utilisateur trouvé</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default UserManagement;
